import type {
  BenefitRow,
  CoverageTier,
  GroupConfig,
  HighlightCard,
  PlanConfig,
  ProductConfig,
  ResourceDisclosure,
  ResourceDoc,
} from './types';
import {COVERAGE_TIER_LABELS, accessCodeEntryMode, groupRequiresAccessCode} from './types';
import {siteFaq} from './defaults/faq';
import {isPdfHref} from './siteLinks';
import {sitePayPeriod} from './payPeriod';

// One change between two configs. `before`/`after` are display strings; a `changed` with
// neither is a coarse flag for a block too large to show inline — `note` overrides "edited".
export interface ConfigChange {
  section: string;
  label: string;
  kind: 'added' | 'removed' | 'changed';
  before?: string;
  after?: string;
  note?: string;
}

const TIERS = Object.keys(COVERAGE_TIER_LABELS) as CoverageTier[];
const money = (n: number | undefined) => `$${(n ?? 0).toFixed(2)}`;
const str = (v: unknown) => (v == null ? '' : String(v));

function diffScalar(
  out: ConfigChange[],
  section: string,
  label: string,
  before: unknown,
  after: unknown,
): void {
  const b = str(before);
  const a = str(after);
  if (b === a) return;
  if (!b) out.push({section, label, kind: 'added', after: a});
  else if (!a) out.push({section, label, kind: 'removed', before: b});
  else out.push({section, label, kind: 'changed', before: b, after: a});
}

// Coarse flag for a block too large to show inline — flags that it changed at all.
function diffBlock(out: ConfigChange[], section: string, label: string, before: unknown, after: unknown): void {
  if (JSON.stringify(before ?? null) !== JSON.stringify(after ?? null)) {
    out.push({section, label, kind: 'changed'});
  }
}

// A field that renders a shared default when omitted: report the default/custom flip,
// otherwise compare the custom blocks.
function diffDefaultable(out: ConfigChange[], section: string, label: string, before: unknown, after: unknown): void {
  const b = before === undefined;
  const a = after === undefined;
  if (b !== a) {
    out.push({section, label, kind: 'changed', before: b ? 'Shared default' : 'Custom', after: a ? 'Shared default' : 'Custom'});
  } else if (!a) {
    diffBlock(out, section, label, before, after);
  }
}

// Short, readable name for an asset — never the raw value (a base64 data URI or long URL is
// meaningless in a diff). Returns "embedded image" or the file's clean name.
function assetName(v: unknown): string {
  const s = str(v);
  if (!s) return '';
  if (s.startsWith('data:')) return 'embedded image';
  const last = s.split('?')[0].split('/').pop() || s;
  return decodeURIComponent(last).replace(/^\d{10,}-[0-9a-f]{8,}-/i, '');
}

// Diff an asset field: detect the change on the raw value, but only display the short name.
// A same-named replacement reads as "replaced".
function diffAsset(out: ConfigChange[], section: string, label: string, before: unknown, after: unknown): void {
  const b = str(before);
  const a = str(after);
  if (b === a) return;
  const bn = assetName(before);
  const an = assetName(after);
  if (!b) out.push({section, label, kind: 'added', after: an});
  else if (!a) out.push({section, label, kind: 'removed', before: bn});
  else if (bn === an) out.push({section, label, kind: 'changed', note: 'replaced'});
  else out.push({section, label, kind: 'changed', before: bn, after: an});
}

function diffPlan(out: ConfigChange[], prefix: string, before: PlanConfig, after: PlanConfig): void {
  const section = 'Products & plans';
  diffScalar(out, section, `${prefix} › Name`, before.name, after.name);
  diffScalar(out, section, `${prefix} › Tagline`, before.tagline, after.tagline);
  diffScalar(out, section, `${prefix} › Network`, before.network, after.network);
  diffAsset(out, section, `${prefix} › Benefits PDF`, before.sbcUrl, after.sbcUrl);
  for (const tier of TIERS) {
    const b = before.rates?.[tier] ?? 0;
    const a = after.rates?.[tier] ?? 0;
    if (b !== a) {
      out.push({
        section,
        label: `${prefix} › ${COVERAGE_TIER_LABELS[tier]} rate`,
        kind: 'changed',
        before: money(b),
        after: money(a),
      });
    }
  }
  if (JSON.stringify(before.highlights ?? []) !== JSON.stringify(after.highlights ?? [])) {
    out.push({section, label: `${prefix} › Highlights`, kind: 'changed'});
  }
}

const onOff = (on: boolean) => (on ? 'On' : 'Off');
// Matches the builder's "Waiving coverage" choices.
const waiveLabel = (p: ProductConfig) =>
  p.declinable === false ? 'No prompt' : p.declineStyle === 'skipped' ? 'Skip' : 'Decline (recorded)';

const headlineText = (segs: {text: string}[] | undefined) => (segs ?? []).map((s) => s.text).join('');
const truncate = (s: string, n = 48) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// Diff a list of text items (FAQ, steps) matched by a natural key, showing the
// before → after of each text field. Added/removed items are flagged by the key.
function diffTextList<T>(
  out: ConfigChange[],
  section: string,
  prefix: string,
  before: T[],
  after: T[],
  key: (item: T) => string,
  fields: Array<{label: string; get: (item: T) => string | undefined}>,
): void {
  const byKeyBefore = new Map(before.map((i) => [key(i), i]));
  const byKeyAfter = new Map(after.map((i) => [key(i), i]));
  const keys = [...after.map(key), ...before.map(key).filter((k) => !byKeyAfter.has(k))];
  const seen = new Set<string>();
  for (const k of keys) {
    if (seen.has(k)) continue;
    seen.add(k);
    const b = byKeyBefore.get(k);
    const a = byKeyAfter.get(k);
    const label = `${prefix} › “${truncate(k)}”`;
    if (b && !a) out.push({section, label, kind: 'removed'});
    else if (!b && a) out.push({section, label, kind: 'added'});
    else if (b && a) {
      for (const f of fields) diffScalar(out, section, `${label} › ${f.label}`, f.get(b), f.get(a));
    }
  }
}

// Uploaded files read by name; site pages and web links by their path/URL.
const linkName = (href: string) =>
  isPdfHref(href) || href.includes('/uploads/') ? assetName(href) : href;

const highlightMode = (card: HighlightCard | null | undefined) =>
  card === undefined ? 'Shared default' : card === null ? 'None' : 'Custom';

interface ResourcePage {
  title?: string;
  intro: string;
  documents: ResourceDoc[];
  disclosures?: ResourceDisclosure[];
}

function diffResourcePage<P extends ResourcePage>(
  out: ConfigChange[],
  section: string,
  page: string,
  before: P | undefined,
  after: P | undefined,
  highlight: (p: P) => HighlightCard | null | undefined,
): void {
  if (!before || !after) {
    if (before) out.push({section, label: page, kind: 'removed'});
    if (after) out.push({section, label: page, kind: 'added'});
    return;
  }
  diffScalar(out, section, `${page} › Title`, before.title, after.title);
  diffScalar(out, section, `${page} › Intro`, before.intro, after.intro);
  diffTextList(out, section, `${page} › Document`, before.documents, after.documents, (d) => d.title, [
    {label: 'description', get: (d) => d.description},
    {label: 'link', get: (d) => linkName(d.href)},
  ]);
  const bh = highlight(before);
  const ah = highlight(after);
  if (highlightMode(bh) !== highlightMode(ah)) {
    out.push({section, label: `${page} › Highlight card`, kind: 'changed', before: highlightMode(bh), after: highlightMode(ah)});
  } else {
    diffBlock(out, section, `${page} › Highlight card`, bh, ah);
  }
  diffDefaultable(out, section, `${page} › Disclosures`, before.disclosures, after.disclosures);
}

// A section-grouped, human-readable diff of the render config — the "before" side is
// the currently-published config, the "after" side is the draft being published.
export function diffGroupConfigs(before: GroupConfig, after: GroupConfig): ConfigChange[] {
  const out: ConfigChange[] = [];

  const brand = 'Brand & identity';
  diffScalar(out, brand, 'Site name', before.name, after.name);
  diffScalar(out, brand, 'Primary color', before.primaryColor, after.primaryColor);
  diffAsset(out, brand, 'Logo', before.logoUrl, after.logoUrl);
  diffScalar(out, brand, 'Banner', before.banner, after.banner);

  const support = 'Support & coverage';
  diffScalar(out, support, 'Coverage period', before.coveragePeriod, after.coveragePeriod);
  diffScalar(out, support, 'Support phone', before.supportPhone, after.supportPhone);
  diffScalar(out, support, 'Support text line', before.supportText, after.supportText);
  diffScalar(out, support, 'Support email', before.supportEmail, after.supportEmail);
  diffScalar(out, support, 'Benefits contact', before.benefitsContact, after.benefitsContact);
  diffAsset(out, support, 'Comparison PDF', before.comparisonPdfUrl, after.comparisonPdfUrl);

  const gate = 'Access-code gate';
  diffScalar(out, gate, 'Access-code term', before.accessCodeTerm, after.accessCodeTerm);
  if (groupRequiresAccessCode(before) !== groupRequiresAccessCode(after)) {
    out.push({
      section: gate,
      label: 'Access code required',
      kind: 'changed',
      before: groupRequiresAccessCode(before) ? 'Yes' : 'No',
      after: groupRequiresAccessCode(after) ? 'Yes' : 'No',
    });
  }

  // Products, matched by category; plans within them matched by id.
  const products = 'Products & plans';
  diffScalar(out, products, 'Pay period', sitePayPeriod(before).label, sitePayPeriod(after).label);
  const beforeProducts = new Map(before.products.map((p) => [p.category, p]));
  const afterProducts = new Map(after.products.map((p) => [p.category, p]));
  const categories = [
    ...after.products.map((p) => p.category),
    ...before.products.map((p) => p.category).filter((c) => !afterProducts.has(c)),
  ];
  const beforePlans = new Map(before.plans.map((p) => [p.id, p]));
  const afterPlans = new Map(after.plans.map((p) => [p.id, p]));

  for (const category of categories) {
    const bp = beforeProducts.get(category);
    const ap = afterProducts.get(category);
    const name = ap?.label ?? bp?.label ?? category;
    if (bp && !ap) {
      out.push({section: products, label: name, kind: 'removed', before: 'Product'});
      continue;
    }
    if (!bp && ap) {
      out.push({section: products, label: name, kind: 'added', after: 'Product'});
      continue;
    }
    if (!bp || !ap) continue;
    diffScalar(out, products, `${name} › Label`, bp.label, ap.label);
    diffScalar(out, products, `${name} › Benefits table title`, bp.benefitsTableTitle, ap.benefitsTableTitle);
    diffScalar(out, products, `${name} › Description`, bp.description, ap.description);
    diffAsset(out, products, `${name} › Step video`, bp.videoUrl, ap.videoUrl);
    diffScalar(out, products, `${name} › Waiving coverage`, waiveLabel(bp), waiveLabel(ap));
    diffScalar(out, products, `${name} › Coverage tiers`, onOff(bp.tierSelectable !== false), onOff(ap.tierSelectable !== false));
    diffScalar(out, products, `${name} › Lowest cost badge`, bp.hideLowestCostBadge ? 'Hidden' : 'Shown', ap.hideLowestCostBadge ? 'Hidden' : 'Shown');

    const beforeRows = bp.benefitRows ?? [];
    const afterRows = ap.benefitRows ?? [];
    if (JSON.stringify(beforeRows) !== JSON.stringify(afterRows)) {
      // Order-insensitive compare: same rows in a new order => a pure reorder.
      const orderless = (rows: BenefitRow[]) => rows.map((r) => JSON.stringify(r)).sort().join(' ');
      const reordered = beforeRows.length > 0 && orderless(beforeRows) === orderless(afterRows);
      out.push({
        section: products,
        label: `${name} › Benefits table`,
        kind: 'changed',
        note: reordered ? 'reordered' : undefined,
      });
    }

    const planIds = [...ap.planIds, ...bp.planIds.filter((id) => !ap.planIds.includes(id))];
    for (const id of planIds) {
      const bpl = beforePlans.get(id);
      const apl = afterPlans.get(id);
      const planName = apl?.name || bpl?.name || 'Untitled plan';
      const prefix = `${name} › ${planName}`;
      if (bpl && !apl) out.push({section: products, label: prefix, kind: 'removed', before: 'Plan'});
      else if (!bpl && apl) out.push({section: products, label: prefix, kind: 'added', after: 'Plan'});
      else if (bpl && apl) diffPlan(out, prefix, bpl, apl);
    }

    // Reordering the plans common to both — shown as the name sequence before → after.
    const commonBefore = bp.planIds.filter((id) => ap.planIds.includes(id));
    const commonAfter = ap.planIds.filter((id) => bp.planIds.includes(id));
    if (commonBefore.length >= 2 && JSON.stringify(commonBefore) !== JSON.stringify(commonAfter)) {
      const seq = (ids: string[]) =>
        ids.map((id) => (afterPlans.get(id) ?? beforePlans.get(id))?.name || 'Untitled plan').join(' · ');
      out.push({
        section: products,
        label: `${name} › Plan order`,
        kind: 'changed',
        before: seq(commonBefore),
        after: seq(commonAfter),
      });
    }
  }

  const landing = 'Landing & hero';
  diffScalar(out, landing, 'Hero headline', headlineText(before.heroHeadline), headlineText(after.heroHeadline));
  diffScalar(out, landing, 'Hero tagline', before.heroTagline, after.heroTagline);
  diffScalar(out, landing, 'Hero blurb', before.heroBlurb, after.heroBlurb);
  diffBlock(out, landing, 'Coverage cards', before.coverageCards, after.coverageCards);
  diffDefaultable(out, landing, 'Care cards', before.careCards, after.careCards);

  diffScalar(out, gate, 'Headline', headlineText(before.accessCodeScreen?.headline), headlineText(after.accessCodeScreen?.headline));
  diffScalar(out, gate, 'Blurb', before.accessCodeScreen?.blurb, after.accessCodeScreen?.blurb);
  diffScalar(out, gate, 'Code placeholder', before.accessCodeScreen?.placeholder, after.accessCodeScreen?.placeholder);
  const entryLabel = (c: GroupConfig) => (accessCodeEntryMode(c) === 'dropdown' ? 'Pick from a list' : 'Type a code');
  diffScalar(out, gate, 'Location entry', entryLabel(before), entryLabel(after));

  diffDefaultable(out, 'FAQ', 'FAQ page', before.faq, after.faq);
  diffTextList(
    out,
    'FAQ',
    'FAQ',
    siteFaq(before).flatMap((s) => s.items),
    siteFaq(after).flatMap((s) => s.items),
    (i) => i.question,
    [{label: 'answer', get: (i) => i.answer}],
  );

  const confirm = 'Confirmation';
  diffScalar(out, confirm, 'Effective-date note', before.confirmation?.effectiveDateNote, after.confirmation?.effectiveDateNote);
  diffTextList(out, confirm, 'What happens next', before.confirmation?.whatHappensNext ?? [], after.confirmation?.whatHappensNext ?? [], (i) => i.title, [
    {label: 'body', get: (i) => i.body},
  ]);
  diffTextList(out, confirm, 'FAQ', before.confirmation?.faq ?? [], after.confirmation?.faq ?? [], (i) => i.question, [
    {label: 'answer', get: (i) => i.answer},
  ]);

  const howto = 'How to enroll';
  if (before.howToEnroll && after.howToEnroll) {
    diffScalar(out, howto, 'Subtitle', before.howToEnroll.subtitle, after.howToEnroll.subtitle);
    diffTextList(out, howto, 'Step', before.howToEnroll.steps, after.howToEnroll.steps, (i) => i.title, [
      {label: 'body', get: (i) => i.body},
    ]);
  } else {
    diffDefaultable(out, howto, 'How to enroll page', before.howToEnroll, after.howToEnroll);
  }

  const other = 'Content';
  diffBlock(out, other, 'Checkout options', before.checkout, after.checkout);

  const resources = 'Resources';
  const shown = (c: GroupConfig) => (c.resourcesHidden ? 'Off' : 'On');
  diffScalar(out, resources, 'Resources section', shown(before), shown(after));
  const bs = before.resourcesSection;
  const as = after.resourcesSection;
  diffScalar(out, resources, 'Landing cards › Section title', bs?.title, as?.title);
  diffScalar(out, resources, 'Landing cards › Intro', bs?.intro, as?.intro);
  diffTextList(out, resources, 'Landing card', bs?.items ?? [], as?.items ?? [], (i) => i.title, [
    {label: 'icon', get: (i) => i.icon},
    {label: 'description', get: (i) => i.description},
    {label: 'link', get: (i) => linkName(i.href)},
  ]);
  diffResourcePage(out, resources, 'Plan resources page', before.resourcesPage, after.resourcesPage, (p) => p.virtualCare);
  diffResourcePage(out, resources, 'Annual notices page', before.noticesPage, after.noticesPage, (p) => p.highlight);

  return out;
}
